import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowUp, CircleStop } from 'lucide-react';
import AGTButton from '../../common/AGTButton';
import { useTranslation } from 'react-i18next';

// Un solo contenedor simplificado
const InputContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : isDarkMode
        ? '#1C1C1E'
        : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '12px',
    padding: '8px',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.15)'
      : 'none',
  };
});

const StyledTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      borderRadius: '8px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
      color: isWallpaperMode || isDarkMode ? '#FFFFFF' : '#000000',
      '&::placeholder': {
        color: isWallpaperMode || isDarkMode
          ? 'rgba(255, 255, 255, 0.5)'
          : 'rgba(0, 0, 0, 0.5)',
        opacity: 1,
      },
    },
  };
});

/**
 * OptimizedInput - Nuevo componente de input simplificado
 *
 * Este es un placeholder temporal que mantiene la misma interfaz
 * que el OptimizedInput anterior pero con una UI más simple.
 */
const OptimizedInput = forwardRef(({
  onSendMessage,
  isWaitingForResponse,
  isStreaming = false,
  onStopStreaming,
  rateLimitInfo = null,
  onOpenAGTPromo,
  selectedModel = 'agt-nest-20250314',
  onModelChange,
  selectedPersonality = 'normal',
  onPersonalityChange,
  isPro = false,
  error = null,
  onErrorClose,
  lastAssistantMessage,
  onPromptAction = () => {},
  onProjectClick,
  onRecordingStateChange = () => {},
}, ref) => {
  const { t } = useTranslation();
  const [inputMessage, setInputMessage] = useState('');
  const textFieldRef = useRef(null);

  // Exponer métodos para compatibilidad con el componente anterior
  useImperativeHandle(ref, () => ({
    focus: () => {
      textFieldRef.current?.focus();
    },
    handlePromptAction: (action) => {
      const promptText = typeof action === 'string'
        ? action
        : (action?.prompt || action?.text || '');

      if (promptText) {
        setInputMessage(promptText);
        textFieldRef.current?.focus();
      }
    },
    getTextContent: () => inputMessage,
    clearContent: () => {
      setInputMessage('');
    }
  }));

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const handleSendClick = () => {
    if (inputMessage.trim() && !isWaitingForResponse) {
      onSendMessage({ text: inputMessage });
      setInputMessage('');
    }
  };

  const handleStopClick = () => {
    if (onStopStreaming) {
      onStopStreaming();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isWaitingForResponse && !isStreaming) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const hasContent = inputMessage.trim().length > 0;
  // Mostrar botón si hay contenido, está esperando respuesta, o está streaming
  const showButton = hasContent || isWaitingForResponse || isStreaming;

  return (
    <InputContainer>
      <StyledTextField
        inputRef={textFieldRef}
        value={inputMessage}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        placeholder={isWaitingForResponse ? t('input.waitingResponse') : t('input.askAGT')}
        disabled={isWaitingForResponse}
        multiline
        maxRows={4}
        fullWidth
      />

      {showButton && (
        <AGTButton
          variant="primary"
          icon={isStreaming ? <CircleStop size={20} /> : <ArrowUp size={20} />}
          onClick={isStreaming ? handleStopClick : handleSendClick}
          disabled={isWaitingForResponse && !isStreaming}
          size="medium"
          sx={{
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            borderRadius: '10px'
          }}
        />
      )}
    </InputContainer>
  );
});

OptimizedInput.displayName = 'OptimizedInput';

// Componente UpgradeButton para compatibilidad
OptimizedInput.UpgradeButton = React.memo(({ onClick, fullWidth }) => {
  const { t } = useTranslation();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(3, 133, 255, 0.1)',
        color: '#0385FF',
        borderRadius: '28px',
        padding: '8px 16px',
        fontWeight: 500,
        fontSize: '14px',
        cursor: 'pointer',
        border: '1px solid rgba(3, 133, 255, 0.3)',
        transition: 'all 0.2s ease',
        width: fullWidth ? '100%' : 'auto',
        height: '36px',
        '&:hover': {
          backgroundColor: 'rgba(3, 133, 255, 0.15)',
        }
      }}
    >
      {t('model.upgradeToPro')}
    </Box>
  );
});

OptimizedInput.UpgradeButton.displayName = 'UpgradeButton';

export default React.memo(OptimizedInput);
